// src/i18n.js
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

/**
 * Which namespaces get preloaded before the very first render (the one that
 * has to match the prerendered HTML exactly, or hydration throws).
 *
 * This used to be EVERY namespace in the app - 18 separate JSON fetches on
 * every single page load, including the ~230KB faq.json, even for a visitor
 * who only ever looked at the homepage. That was a deliberate (if heavy-
 * handed) fix for a real bug: react: { useSuspense: true } means a component
 * whose namespace hasn't loaded yet suspends on its very first render, and
 * with no <Suspense> boundary anywhere in the app to catch that, an
 * unloaded namespace crashed the whole page ("Minified React error #418").
 * footer.jsx renders on every page and was hitting exactly that.
 *
 * The actual requirement is narrower than "preload everything": only the
 * namespaces that some component in the tree will call useTranslation() for
 * during the FIRST render need to be ready before hydrateRoot runs. That's
 * always 'translation' (the default NS - header, the book-appointment
 * button, the language switcher, the cookie banner all use it with no NS
 * argument) and 'footer' (renders on every page), plus 'pageNotFound' (cheap,
 * and the fallback for anything unmatched - a bad :lang segment, a stray
 * path), plus whichever ONE namespace the current page itself needs - see
 * getPageNamespace() below, which mirrors getLangFromUrl()'s URL parsing to
 * figure out which page is being requested.
 *
 * A client-side navigation to a page whose namespace wasn't preloaded still
 * needs to not crash. A <Suspense> boundary around AppRoutes was tried for
 * that, but it turned a proven-safe hydration into a reproducible "Minified
 * React error #418" on the very FIRST hydration of every page - react-snap's
 * prerendered HTML is a plain headless-browser snapshot (no server-Suspense
 * dehydration markers), and React's hydration algorithm for a <Suspense>
 * boundary expects those markers regardless of whether anything actually
 * suspends. So react: { useSuspense: false } below instead of a Suspense
 * boundary: a component whose namespace isn't loaded yet just renders the
 * translation key literally for one render, then re-renders with the real
 * text once the fetch resolves - a small, self-healing visual blip on
 * in-app navigation to a not-yet-visited page, never a crash, and it leaves
 * the hydrated tree shape identical to the original working version.
 */
const CORE_NAMESPACES = ['translation', 'footer', 'pageNotFound'];

// Mirrors each page component's own useTranslation('x') / <SEOUpdater page="x" />
// call - see approutes.jsx for the matching route list.
const PAGE_NAMESPACE_BY_SEGMENT = {
  '': 'homePage',
  about: 'aboutPage',
  immigration: 'immigration',
  personalinjury: 'personalInjury',
  lemonlaw: 'lemonLaw',
  employmentlaw: 'employmentLaw',
  team: 'teamPage',
  blog: 'blogPage', // covers both /blog and /blog/:blogTitle
  faq: 'faq',
  'contact-us': 'contactUs',
  accessibility: 'accPage',
  'privacy-policy': 'privacyPolicy',
  'legal-disclaimer': 'legalDisclaimer',
};

const getPageNamespace = () => {
  if (window.location.pathname === '/404.html') return 'pageNotFound';
  const match = window.location.pathname.match(/^\/(?:en|hy|ru|es)?\/?([^/]*)/);
  const segment = match ? match[1] : '';
  return PAGE_NAMESPACE_BY_SEGMENT[segment]; // undefined is fine - filtered out below
};

const getInitialNamespaces = () => {
  const pageNs = getPageNamespace();
  return pageNs ? [...CORE_NAMESPACES, pageNs] : CORE_NAMESPACES;
};
/**
 * Mirrors withLang.js's getCurrentLang() exactly - matches the URL's lang
 * segment, defaulting to 'en' for "/" or "/404.html" or anything else
 * unmatched.
 *
 * Without this, i18next has no explicit `lng` at init(), so
 * LanguageDetector's `order` decides the STARTING language - and
 * 'navigator' sits before 'htmlTag' in that order, so on a completely
 * fresh browser (no stored preference) it ALWAYS wins, reporting whatever
 * OS/browser language the visitor has configured, completely ignoring which
 * URL they actually requested. On the very first render - the one that has
 * to match the prerendered HTML exactly, or hydration throws - i18n.language
 * was "whatever the visitor's browser reports", not "whatever this page
 * actually is". LanguageRouterWrapper's useEffect does call
 * i18n.changeLanguage(lang) to correct this, but that only runs AFTER the
 * initial render/mount, so it was always one render too late: the
 * prerendered HTML for /hy/... is Armenian, the freshly-hydrating client
 * started out in whatever the browser reports (English, in the overwhelming
 * majority of real visits from this site's audience), and hydration doesn't
 * tolerate a full-page language mismatch. Confirmed directly - every /en/
 * and root "/" page always hydrated cleanly (nothing to mismatch, English
 * is also the fallback), while /hy/, /ru/ and /es/ pages threw "Minified
 * React error #418" on a genuinely fresh load, every single time, in every
 * browser tested. Setting `lng` explicitly here takes priority over
 * LanguageDetector entirely (that's documented i18next behavior - an
 * explicit `lng` skips detection), so the correct language is already in
 * place before the very first render.
 */
const getLangFromUrl = () => {
  const match = window.location.pathname.match(/^\/(en|hy|ru|es)(\/|$)/);
  return match ? match[1] : 'en';
};

const i18nInitPromise = i18n
  .use(HttpBackend) // load translations from /public/locales
  .use(LanguageDetector) // detect user language (cookie, localStorage, navigator, etc.)
  .use(initReactI18next) // pass i18n to react-i18next
  .init({
    lng: getLangFromUrl(),
    fallbackLng: 'en',
    supportedLngs: ['en', 'hy', 'ru', 'es'],
    ns: getInitialNamespaces(),
    defaultNS: 'translation',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['querystring', 'localStorage', 'cookie', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'cookie'],
    },
    interpolation: { escapeValue: false }, // React already does XSS protection
    react: { useSuspense: false },
  });

export default i18n;
export { i18nInitPromise };
